import {
  TicketingIntegrationTicketField,
  WorkflowInputType
} from '@kindo/universal';
import { useEffect, useState } from 'react';

import { getIntegrationInputFields } from '../WorkflowIntegrationInput.utils';

import IntegrationInputFieldSelector from './IntegrationInputFieldSelector';

import {
  Button,
  ButtonType,
  Icon,
  IconButton,
  IconButtonType,
  TextIconColor,
  Typography,
  TypographyCasing,
  TypographySize,
  TypographyWeight
} from '~/components/core';
import useActiveBuilderStepIntegrationInput from '~/hooks/useActiveBuilderStepIntegrationInput';
import {
  BuilderWorkflowLlmStep,
  IntegrationWorkflowBuilderStepInput,
  IntegrationWorkflowInput,
  isIntegrationWorkflowBuilderStepInput
} from '~/types';

interface WorkflowIntegrationInputEditorProps {
  activeStep: BuilderWorkflowLlmStep;
  integrationInputActiveInEditor: IntegrationWorkflowBuilderStepInput | null;
  onClose: () => void;
  workflowIntegrationInput: IntegrationWorkflowInput;
}

const WorkflowIntegrationInputEditor: React.FC<
  WorkflowIntegrationInputEditorProps
> = ({
  activeStep,
  onClose,
  workflowIntegrationInput,
  integrationInputActiveInEditor
}) => {
  const [selectedFields, setSelectedFields] = useState<
    TicketingIntegrationTicketField[]
  >([]);

  const { addIntegrationInput, removeIntegrationInput } =
    useActiveBuilderStepIntegrationInput({ activeStep });

  const [selectedWorkflowInputId, setSelectedWorkflowInputId] =
    useState<string>(workflowIntegrationInput.id);

  const workflowInputFieldOptions = getIntegrationInputFields(
    WorkflowInputType.TICKETING_INTEGRATION_TICKET
  ).filter((field) => field !== TicketingIntegrationTicketField.MERGE_ID);

  const workflowStepExistingInput = activeStep.inputs
    .filter(isIntegrationWorkflowBuilderStepInput)
    .find((input) => input.id === selectedWorkflowInputId);

  useEffect(() => {
    if (workflowStepExistingInput) {
      setSelectedFields(
        workflowStepExistingInput.config?.specifiedFields ?? []
      );
    }
  }, [workflowStepExistingInput]);

  useEffect(() => {
    if (integrationInputActiveInEditor) {
      setSelectedWorkflowInputId(integrationInputActiveInEditor.id);
      setSelectedFields(
        integrationInputActiveInEditor.config?.specifiedFields ?? []
      );
    }
  }, [integrationInputActiveInEditor]);

  const handleAddOrUpdateIntegrationInput = () => {
    addIntegrationInput({
      ...workflowIntegrationInput,
      config: {
        specifiedFields: selectedFields
      }
    });
    onClose();
  };

  const canSaveIntegrationInput =
    selectedFields.length !== 0 &&
    selectedWorkflowInputId &&
    !(
      workflowStepExistingInput &&
      workflowStepExistingInput.config?.specifiedFields === selectedFields
    );

  return (
    <div
      className="flex flex-col gap-4 rounded-lg p-6 shadow-lg"
      data-testid="integration-input-builder-container"
    >
      <div
        className="flex w-full flex-col items-start gap-2"
        data-testid="input-field-selection-container"
      >
        <div
          className="flex w-full flex-row items-center justify-between gap-2"
          data-testid="input-builder-header-container"
        >
          {/* Title */}
          <Typography
            casing={TypographyCasing.UPPERCASE}
            color={TextIconColor.PRIMARY}
            size={TypographySize.SMALL}
            weight={TypographyWeight.MEDIUM}
          >
            fields
          </Typography>
          <div
            className="flex flex-row gap-2"
            data-testid="input-builder-header-text-container"
          >
            {/* Select all/deselect all */}
            <button
              className="flex cursor-pointer transition-opacity hover:opacity-80"
              data-testid="select-all-button"
            >
              <Typography
                color={TextIconColor.PRIMARY_HIGHLIGHT}
                italic
                onClick={
                  selectedFields.length === 0
                    ? () => setSelectedFields(workflowInputFieldOptions)
                    : () => setSelectedFields([])
                }
                size={TypographySize.SMALL}
                weight={TypographyWeight.SEMI_LIGHT}
              >
                {selectedFields.length === 0 ? 'Select all' : 'Deselect all'}
              </Typography>
            </button>
            {/* Selected fields */}
            <Typography
              color={TextIconColor.SECONDARY}
              italic
              size={TypographySize.SMALL}
              weight={TypographyWeight.SEMI_LIGHT}
            >
              {`${selectedFields.length} selected`}
            </Typography>
          </div>
        </div>
        <IntegrationInputFieldSelector<TicketingIntegrationTicketField>
          integrationInputFields={workflowInputFieldOptions}
          integrationInputType={WorkflowInputType.TICKETING_INTEGRATION_TICKET}
          selectedInputFields={selectedFields}
          setSelectedInputFields={setSelectedFields}
        />
      </div>

      <div
        className="flex w-full flex-row items-end justify-between pt-2"
        data-testid="footer-container"
      >
        <IconButton
          color={TextIconColor.PRIMARY}
          disabled={!workflowStepExistingInput}
          hidden={!workflowStepExistingInput}
          icon={Icon.TRASH}
          onClick={() => {
            removeIntegrationInput(selectedWorkflowInputId);
            onClose();
          }}
          tooltip="Remove input from workflow step"
          type={IconButtonType.OUTLINED}
        />
        <div
          className="flex items-end gap-2"
          data-testid="save-cancel-buttons-container"
        >
          <Button label="Cancel" onClick={onClose} type={ButtonType.OUTLINED} />
          <Button
            disabled={!canSaveIntegrationInput}
            endIcon={canSaveIntegrationInput ? Icon.CHECK : undefined}
            label={workflowStepExistingInput ? 'Update Input' : 'Add Input'}
            onClick={handleAddOrUpdateIntegrationInput}
            tooltip={
              workflowStepExistingInput ? '' : 'Add input to workflow step'
            }
            type={ButtonType.SOLID_GRADIENT}
          />
        </div>
      </div>
    </div>
  );
};

export default WorkflowIntegrationInputEditor;
